import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EDITOR_PLAYER_ID, isAllowAutoPlay, joinClassNames, STORY_CONFIG_ID } from '../../util/util';
import { setAdsPlaying, setCurrentInteractiveElement, setCurrentVolumeLevel, setIsLiveStarted, setIsLiveStopped, setIsPlaying, setMediaWithDuration, setPlayerCurrentPosition, setPlayerDuration, setPlayerReady, setRemovePoster, setShowChatWindow } from '../../actions/playerActions';
import { CHANGE_SCENE_TYPES, PLAYER_TRANSITIONS_OUT } from '../../util/constants';
import { setChangeSceneType, setChapters, setChatActivated, setCloseIncomingMessage, setCurrentMediaId, setIsMediaLive, setMediasData, setSceneConfig, setSceneHistory, setRestrictedMedias, setTransitionEffectStatus, setTransitionLayer, setTransitionOut, setVideoDownloadUrl, setVideoHoverUrl, setViewerEngagementModuleActivated, updateSceneHistory, setIsMaintainProportions } from '../../actions/configurationActions';
import { findHighestQualityVideo } from '../../util/media-data-util';
import { LiveHelper } from '../../util/live-helper';
import { NumberHelper } from '../../util/number-helper';
import { MessageHelper } from '../../util/message-helper';
import { setShowMobileChat } from '../../actions/chatLoginActions';
import { sendMediaGAPauseData, sendMediaGAPlayData, sendMediaGAViewData } from '../../util/gAUtil';

import { PlayerHelper } from './player-helper';
import { FetchDataHelper } from './hooks/fetch-data-helper';

import './Player.scss';

const Player = (props: PlayerHelper.PlayerProps) => {
    const dispatch = useDispatch();
    const { playerId, isVisible, token, playerMedia, settings, queryParams, classNames, handlePlayerHover, isMaintainProportions } = props;

    const createdPlayerInstance = React.useRef(false);
    const checkpointRef = React.useRef(true);
    const positionMessage = React.useRef('no-notify');
    const liveStoppingTime = React.useRef<number | null>(null);

    const [loading, setLoading] = React.useState(true);
    const [isSendViewGAEvent, setIsSendViewGAEvent] = React.useState(false);

    const { currentPosition, duration, mediaDurations, isPlaying, currentVolumeLevel, isPosterRemoved } = useSelector((state: any) => state.playerReducer);
    const { isCloseIncomingMessage, viewerEngagementModuleActivated, isChatActivated, mediaName, accountId, isMediaLive, sceneConfig, mediasData, playlistId, mediaType, thumbnailUrl, scenario, shouldChangeCurrentMedia, changeSceneType, isTransitionLayerEnable, showCountdownModule, currentMediaId, restrictedMedias, minimizedPlayer } = useSelector((state: any) => state.configurationReducer);

    const getStateRef = useCallback(() => ({
        props: props,
        isMediaLive: isMediaLive,
        isPlaying: isPlaying,
        duration: duration,
        mediaType: mediaType,
        mediasData:mediasData,
        sceneConfig: sceneConfig,
        scenario: scenario,
        isTransitionLayerEnable: isTransitionLayerEnable,
        currentPosition: currentPosition,
        mediaName: mediaName,
        accountId: accountId,
        shouldChangeCurrentMedia: shouldChangeCurrentMedia,
        changeSceneType: changeSceneType,
        currentMediaId: currentMediaId,
        currentVolumeLevel: currentVolumeLevel,
        isPosterRemoved: isPosterRemoved,
        mediaDurations: mediaDurations,
        playlistId: playlistId,
        restrictedMedias: restrictedMedias,
        queryParams: queryParams
    }), [accountId, changeSceneType, currentMediaId, currentPosition, currentVolumeLevel, duration, isMediaLive, isPlaying, isPosterRemoved, isTransitionLayerEnable, mediaDurations, mediaName, mediaType, mediasData, playlistId, props, scenario, sceneConfig, shouldChangeCurrentMedia, restrictedMedias, queryParams]);

    const latestState = useRef(getStateRef());
    useEffect(() => {
        latestState.current = getStateRef();
    }, [props, isMediaLive, mediaType, isPlaying, duration, mediasData, sceneConfig, scenario, isTransitionLayerEnable, currentPosition, mediaName, accountId, shouldChangeCurrentMedia, changeSceneType, currentMediaId, currentVolumeLevel, isPosterRemoved, mediaDurations, playlistId, getStateRef]);

    const onPlayerReady = useCallback((value) => dispatch(setPlayerReady(value)), [dispatch]);

    const hideControls = useCallback(() => {
        (window as any).GoBrain.widgets()[playerId].pause();
        dispatch(setIsPlaying(false));
        dispatch(setAdsPlaying(true));
    }, [dispatch, playerId]);

    const showControls = useCallback(() => {
        dispatch(setAdsPlaying(false));
        dispatch(setIsPlaying(true));
        (window as any).GoBrain.widgets()[playerId].play();

        const { currentPosition } = latestState.current;
        (window as any).GoBrain.widgets(playerId)?.position?.(currentPosition);
    }, [dispatch, playerId]);

    const turnOfChat = useCallback(() => {
        dispatch(setViewerEngagementModuleActivated(false));
        dispatch(setChatActivated(false));
        dispatch(setCloseIncomingMessage(true));
    }, [dispatch]);

    const updateChatState = useCallback((mediaDataCustom: any) => {
        const shouldActivateViewerEngagementModule = mediaDataCustom?.activatedViewerEngagement && !mediaDataCustom?.hideEngagementModule;
        if (shouldActivateViewerEngagementModule !== viewerEngagementModuleActivated) {
            dispatch(setViewerEngagementModuleActivated(shouldActivateViewerEngagementModule));
        }

        const shouldActivateChat = mediaDataCustom?.activatedChat !== false;
        if (shouldActivateChat !== isChatActivated) {
            dispatch(setChatActivated(shouldActivateChat)); // default is true which means "undefined" should be true
        }

        if (mediaDataCustom?.closeIncomingMessage !== isCloseIncomingMessage) {
            dispatch(setCloseIncomingMessage(!!mediaDataCustom?.closeIncomingMessage));
        }
    }, [dispatch, isChatActivated, isCloseIncomingMessage, viewerEngagementModuleActivated]);

    const onPlayerQueueItem = useCallback(({ currentItem, currentIndex }: any) => {
        const { scenario, restrictedMedias, changeSceneType, currentMediaId, currentVolumeLevel, isPlaying, mediasData, shouldChangeCurrentMedia, props } = latestState.current;
        const { playerId, isVisible, token, playerMedia, isMaintainProportions } = props;

        MessageHelper.sendMessage({
            type: MessageHelper.MESSAGE_TYPES.queueItem,
            mediaId: playerMedia,
            scenarioId: scenario?.id,
            metadata: { queueItem: { currentItem, currentIndex } }
        });

        if (!currentItem && !currentIndex) {
            createdPlayerInstance.current = false;
            return;
        }

        if (currentItem?.playlistId && currentItem.resources?.[0]) {
            dispatch(setIsMediaLive(currentItem.resources[0].live));
            dispatch(setCurrentMediaId(currentItem.id));
        }

        const mediaData = currentItem ? FetchDataHelper.getMediaData({
            currentIndex: currentIndex,
            mediaData: currentItem,
            id: currentItem?.id ?? playerMedia,
            token,
            saveInteractiveElementToStore: (elems: any[]) => dispatch(setCurrentInteractiveElement(elems))
        }) : undefined;

        if (mediaData && !currentItem.playlistId) {
            const newMediasData = mediasData.length ? mediasData.map((m: any) => (m.id === playerMedia ? { ...mediaData, ...m } : m)) : [mediaData];
            dispatch(setMediasData(newMediasData));
            mediaData.live && mediaData.id === currentMediaId && dispatch(setIsMediaLive(true));
        }

        if (restrictedMedias.includes(currentItem?.id)) {
            const newRestrictedMedias = restrictedMedias.filter((id: string) => id !== currentItem?.id);
            dispatch(setRestrictedMedias(newRestrictedMedias));
        }

        if (!isVisible) {
            return;
        }

        if (currentItem?.id) {
            updateChatState(mediaData?.custom ?? {});
            if (mediaData?.live) {
                if (currentItem?.custom?.livemanager) {
                    const isLiveStarted = !!currentItem?.custom?.livemanager?.start && !currentItem?.custom?.livemanager?.stop;
                    const isLiveStopped = !!currentItem?.custom?.livemanager?.start && !!currentItem?.custom?.livemanager?.stop
                        ? currentItem?.custom?.livemanager?.stop
                        : false;

                    if ((!isLiveStarted || isLiveStopped) && currentItem.playlistId) {
                        (window as any).GoBrain.widgets()[playerId].pause();
                        turnOfChat();
                        dispatch(setIsPlaying(undefined));
                    }

                    dispatch(setIsLiveStarted(isLiveStarted));
                    dispatch(setIsLiveStopped(isLiveStopped));
                } else {
                    turnOfChat();
                    dispatch(setIsLiveStarted(true));
                    dispatch(setIsLiveStopped(false));
                }
            }

            const downloadUrl = findHighestQualityVideo(currentItem.resources?.[0]?.downloadables);
            const previewUrl = ((currentItem.resources ?? [])[0].downloadables ?? []).find(({ label }: { label: any }) => label === 'Low')?.url;
            dispatch(setVideoDownloadUrl(downloadUrl ?? ''));
            dispatch(setVideoHoverUrl(previewUrl ?? ''));

            const chapters = currentItem.resources?.[0]?.tracks?.find((track: any) => track.type === 'chapter')?.sequences ?? [];
            dispatch(setChapters(chapters))

            const videoStream = (((currentItem.resources ?? [])[0].streams ?? [])[0].videos ?? []).find((v: any) => v.height && v.width);
            const isMaintainProportionsValue = videoStream && (Math.floor(videoStream.width / videoStream.height * 1000)/1000) === (Math.floor(9 / 16 * 1000)/1000) ? false : isMaintainProportions;
            dispatch(setIsMaintainProportions(isMaintainProportionsValue));

            if (playerId === EDITOR_PLAYER_ID) {
                (window as any).parent.postMessage(
                    {
                        type: 'set-maintain-proportion',
                        isMaintainProportions: isMaintainProportionsValue,
                    },
                    '*'
                );
            }
            if (playerMedia !== currentItem.id) {
                setTimeout(() => {
                    if (shouldChangeCurrentMedia) {
                        dispatch(setCurrentMediaId(currentItem.id));
                        (window as any).GoBrain.widgets()[playerId].pause();
                        (window as any).GoBrain.widgets()[playerId].play();
                    }
                }, 100);
            } else {
                setTimeout(() => {
                    if (currentItem.playlistId) {
                        return;
                    }
                    const shouldPlay = isVisible && (isPlaying || changeSceneType === CHANGE_SCENE_TYPES.onclick);
                    shouldPlay && (window as any).GoBrain.widgets()[playerId].play();
                    shouldPlay && (window as any).GoBrain.widgets()[playerId].volume(currentVolumeLevel);
                }, 100);
            }
        }
    }, [dispatch, turnOfChat, updateChatState]);

    const onPlayerPlayable = useCallback(() => {
        const { changeSceneType, mediaType, scenario, sceneConfig, props } = latestState.current;
        const { playerId, isVisible, settings, queryParams, playerMedia } = props;

        MessageHelper.sendMessage({
            type: MessageHelper.MESSAGE_TYPES.playable,
            mediaId: playerMedia,
            scenarioId: scenario?.id,
            metadata: {}
        });

        const autoplay = playerId === EDITOR_PLAYER_ID ? true : !isVisible ? false : isAllowAutoPlay({ queryParams, settings });
        const defaultSceneBGMediaId = scenario?.defaultSceneBGMediaId ?? "";
        const isDefaultSceneBGMedia = playerMedia === defaultSceneBGMediaId && mediaType === 'scenario';
        if (!isVisible || ((mediaType === 'video' || isDefaultSceneBGMedia) && !autoplay)) {
            return;
        }

        const widget = (window as any).GoBrain.widgets()[playerId];
        widget?.play?.();
        const { defaults } = scenario ?? {};
        if (defaults?.sceneId && defaults?.sceneId === sceneConfig?.currentScene && changeSceneType === CHANGE_SCENE_TYPES.default) {
            if (!settings?.playbackOptions?.replay) {
                widget?.pause?.();
                dispatch(setPlayerCurrentPosition(0))
                dispatch(setIsPlaying(undefined));
            }
        }
    }, [dispatch]);

    const onPlayerPlay = useCallback(() => {
        const { isPlaying, mediaName, accountId, isMediaLive, currentPosition, duration, isPosterRemoved, mediaType, scenario, props } = latestState.current;
        const { playerId, isVisible, playerMedia, settings, handlePlayerHover } = props;

        if (!isVisible) {
            return;
        }

        if (isPlaying === undefined || (isMediaLive && mediaType === 'scenario')) {
            if (isMediaLive && !LiveHelper.isAtLive(isMediaLive, currentPosition, duration)) {
                (window as any).GoBrain.widgets(playerId)?.position?.(LiveHelper.LIVE_POSITION_OFFSET);
            }

            dispatch(setShowChatWindow(
                settings?.engagementFeatures?.enabled && settings?.engagementFeatures?.showChat && settings?.engagementFeatures?.openChatOnPlay
            ));
            dispatch(setShowMobileChat(
                settings?.engagementFeatures?.enabled && settings?.engagementFeatures?.showChat && settings?.engagementFeatures?.openChatOnPlay
            ));

            handlePlayerHover?.();
        }
        dispatch(setIsPlaying(true));

        MessageHelper.sendMessage({ type: MessageHelper.MESSAGE_TYPES.play, mediaId: playerMedia, scenarioId: scenario?.id, metadata: {} });
        window.parent.postMessage(
            {
                origin: 'Qbrick.Interact.Widget.Element',
                identifier: 'qbrick-player-analytics-message',
                command: {
                    type: 'AnalyticsEvent',
                    action: 'click',
                    data: 'play',
                    mediaName: mediaName,
                    mediaId: playerMedia,
                    accountId: accountId,
                    videoLength: duration,
                    isMediaLive: isMediaLive,
                    currentPosition: isMediaLive ? Date.now() : currentPosition,
                },
            },
            '*'
        );

        sendMediaGAPlayData(playerMedia, mediaName);
        if (!isSendViewGAEvent) {
            sendMediaGAViewData(playerMedia, mediaName);
            setIsSendViewGAEvent(true);
        }

        !isPosterRemoved && dispatch(setRemovePoster());
    }, [dispatch, isSendViewGAEvent]);

    const onPlayerPause = useCallback(() => {
        const { currentPosition, mediaName, duration, isMediaLive, accountId, scenario, props } = latestState.current;
        const { isVisible, playerMedia } = props;

        if (!isVisible) {
            return;
        }

        dispatch(setIsPlaying(false));
        MessageHelper.sendMessage({ type: MessageHelper.MESSAGE_TYPES.pause, mediaId: playerMedia, scenarioId: scenario?.id, metadata: {} });
        window.parent.postMessage(
            {
                origin: 'Qbrick.Interact.Widget.Element',
                identifier: 'qbrick-player-analytics-message',
                command: {
                    type: 'AnalyticsEvent',
                    action: 'click',
                    data: 'pause',
                    mediaName: mediaName,
                    mediaId: playerMedia,
                    accountId: accountId,
                    videoLength: duration,
                    isMediaLive: isMediaLive,
                    currentPosition: isMediaLive ? Date.now() : currentPosition,
                },
            },
            '*'
        );

        sendMediaGAPauseData(playerMedia, currentPosition);
    }, [dispatch]);

    const onPlayerPlayComplete = useCallback(() => {
        const { mediasData, scenario, sceneConfig, props } = latestState.current;
        const { playerId, isVisible, playerMedia } = props;

        MessageHelper.sendMessage({
            type: MessageHelper.MESSAGE_TYPES.playComplete,
            mediaId: playerMedia,
            scenarioId: scenario?.id,
            metadata: {}
        });

        if (!isVisible || playerId === EDITOR_PLAYER_ID || !sceneConfig?.nextScene) {
            return;
        }

        dispatch(setChangeSceneType(CHANGE_SCENE_TYPES.default));
        if (sceneConfig?.nextScene !== sceneConfig?.currentScene) {
            if (sceneConfig?.currentScene) {
                dispatch(updateSceneHistory(sceneConfig?.currentScene));
            }

            const currentSceneId = (mediasData ?? []).find((m: any) => m.id === playerMedia)?.sceneId;
            const currentSceneData = currentSceneId && (scenario?.scenes ?? []).find((s: any) => s.id === currentSceneId);

            let transitionOut = PLAYER_TRANSITIONS_OUT.fadeOut;
            if (currentSceneData?.metadata?.transition) {
                transitionOut = currentSceneData.metadata.transition.out ?? PLAYER_TRANSITIONS_OUT.fadeOut;
            }

            // Make sure that transition layer should be trigger 1st. Then others scene change stuffs
            dispatch(setTransitionOut(transitionOut));
            dispatch(setTransitionLayer(true));
            setTimeout(() => {
                dispatch(setSceneConfig({ ...sceneConfig, currentScene: sceneConfig.nextScene }));
            }, 200);
        } else {
            const widget = (window as any).GoBrain.widgets()[playerId];
            widget?.position?.(0);
            widget?.play?.();
        }
    }, [dispatch]);

    const onPlayerPositionChange = useCallback((position: number) => {
        const { isMediaLive, duration, mediaType, isTransitionLayerEnable, scenario, currentPosition, props } = latestState.current;
        const { playerId, isVisible, playerMedia } = props;

        if (!isVisible) {
            return;
        }

        const realPos = isMediaLive ? duration + position : position;
        dispatch(setPlayerCurrentPosition(realPos));

        if (currentPosition <= 0 && checkpointRef.current) {
            checkpointRef.current = false;
			setTimeout(() => {
                isMediaLive && (window as any).GoBrain.widgets(playerId)?.position?.(LiveHelper.LIVE_POSITION_OFFSET);
            });
        }

        if (isMediaLive && liveStoppingTime && liveStoppingTime.current) {
            if (liveStoppingTime.current &&
                    Math.ceil(NumberHelper.precision3(liveStoppingTime.current)) === Math.ceil(NumberHelper.precision3(realPos))) {
                dispatch(setIsPlaying(undefined));
                liveStoppingTime.current = null;
            }
        }

        if (mediaType === 'scenario' && position < 1 && isTransitionLayerEnable) {
            dispatch(setTransitionEffectStatus(true));
        }

        MessageHelper.sendMessage({
            type: MessageHelper.MESSAGE_TYPES.position,
            mediaId: playerMedia,
            scenarioId: scenario?.id,
            metadata: { position: position }
        });

        if (positionMessage.current === 'no-notify') {
            return;
        }

        if (playerId === EDITOR_PLAYER_ID) {
            window.parent.postMessage(
                {
                    type: 'position',
                    position: realPos,
                },
                '*'
            );
        }
    }, [dispatch]);

    const onPlayerDurationChange = useCallback((durationParam: any) => {
        const { mediaDurations, duration, scenario, props } = latestState.current;
        const { isVisible, playerMedia } = props;

        const newDuration = Math.abs(durationParam);
        const mediaDuration = {
            id: playerMedia,
            duration: newDuration,
        };
        if (!mediaDurations.find((m: any) => m.id === mediaDuration.id)) {
            dispatch(setMediaWithDuration([...mediaDurations, mediaDuration]));
        }

        if (isVisible && newDuration !== duration) {
            dispatch(setPlayerDuration(newDuration));
        }

        MessageHelper.sendMessage({
            type: MessageHelper.MESSAGE_TYPES.duration,
            mediaId: playerMedia,
            scenarioId: scenario?.id,
            metadata: { duration: durationParam }
        });
    }, [dispatch]);

    const onPlayerNotificationEvent = useCallback((notification: any) => {
        const { mediasData, isMediaLive, currentMediaId, props } = latestState.current;
        const { playerId, isVisible, settings, queryParams } = props;

        if (!isVisible) {
            return;
        }

        const autoplay = playerId === EDITOR_PLAYER_ID ? true : !isVisible ? false : isAllowAutoPlay({ queryParams, settings });
        if (notification.key === 'PlaybackItem.Track.Sequence.Cover.AttributesSet' && notification.data?.coverId) {
            const isLiveStarted = notification.data?.start === 0 && notification.data?.end === 0;
            const isLiveStopped = notification.data?.start !== 0 && notification.data?.end === null ? notification.data?.start : null;
            liveStoppingTime.current = isLiveStopped;

            dispatch(setIsLiveStarted(isLiveStarted));
            dispatch(setIsLiveStopped(isLiveStopped));
            if (isLiveStarted) {
                autoplay && dispatch(setRemovePoster());
                !autoplay && dispatch(setRemovePoster(false));
            }
        }

		if (notification.key === 'PlaybackItem.Track.Sequence.Cover.Deleted' && !autoplay && isMediaLive) {
			dispatch(setRemovePoster(false));
		}

        if (notification.key === 'PlaybackItem.Track.Sequence.Custom.AttributesSet') {
            if (Object.prototype.hasOwnProperty.call(notification.data, 'start') && typeof notification?.data?.start === "number" && !notification?.data?.end) {
                dispatch(setCurrentInteractiveElement(notification.data));
            } else if (!notification?.data?.custom?.mediaId) {
                dispatch(setCurrentInteractiveElement(null));
            }
        }

        if (notification.key === 'PlaybackItem.CustomPropertySet' && !!notification.data?.customProperty?.value) {
            const newCustom = JSON.parse(notification.data?.customProperty?.value) ?? {};
            const mediaId = notification.data.mediaId;
            const newMediasData = mediasData.map((m: any) => (m.id === mediaId || m.originalId === mediaId ? { ...m, custom: newCustom } : m));
            dispatch(setMediasData(newMediasData));

            if (mediaId === currentMediaId) {
                updateChatState(newCustom);
            }
        }
    }, [dispatch, updateChatState]);

    const onPlayerVolumeChange = useCallback((volumeLevel: number) => {
        const { scenario, props } = latestState.current;
        if (!props.isVisible) {
            return;
        }
        dispatch(setCurrentVolumeLevel(volumeLevel));

        // Send message to communicate with embed sites
        MessageHelper.sendMessage({
            type: MessageHelper.MESSAGE_TYPES.volume,
            mediaId: props.playerMedia,
            scenarioId: scenario?.id,
            metadata: { volume: volumeLevel }
        });

        // Send message to QVP to handle player mute/unmute
        const isPlayerMute = volumeLevel === 0;
        window.parent.postMessage(
            {
                origin: 'Qbrick.Interact.Widget.Element',
                identifier: 'qbrick-modal-handler-event',
                command: {
                    action: 'click',
                    data: isPlayerMute ? 'player-mute' : 'player-unMute',
                },
            },
            '*'
        );
    }, [dispatch]);

    const onPlayerError = useCallback((error: any) => {
        const { props, restrictedMedias} = latestState.current;
        const { playerMedia } = props;
        const response = error?.data?.response ? JSON.parse(error?.data?.response) : {};
        console.info('Player error:', error);
        if (error.code === 403 && response.token === true) {
            createdPlayerInstance.current = false;
            const newRestrictedMedias = [...(restrictedMedias ?? []), playerMedia];
            dispatch(setRestrictedMedias(newRestrictedMedias));
        }

        if (error.name === "Advertisement") {
            showControls();
        }
    }, [dispatch, showControls]);

	const playVisiblePlayer = useCallback(() => {
        if (!playerId) {
            return;
        }

		const widget = (window as any).GoBrain.widgets()[playerId];
        const { currentIndex, queue } = widget?.Queue?.get() ?? {};
		let currentMedia = mediasData.find((media: any) => media?.id === playerMedia || media?.originalId === playerMedia);

        if (!(currentMedia?.id && currentMedia?.mediaDetails) && queue?.[0]) {
            currentMedia = FetchDataHelper.getMediaData({
                mediaData:  queue?.[0],
                id:  queue?.[0]?.id ?? playerMedia,
                token
            });
        }

		const isCurrentMediaLive = currentMedia?.live;
		const isLiveStarted = !!currentMedia?.custom?.livemanager?.start;
		const isLiveStopped = isLiveStarted && !!currentMedia?.custom?.livemanager?.stop;

		if (playerId === EDITOR_PLAYER_ID) {
			return;
		}

		if (isVisible) {
            dispatch(setIsLiveStarted(isLiveStarted));
            dispatch(setIsLiveStopped(isLiveStopped));

            if (mediaType === 'scenario') {
                // Not show controls when live haven't started or is stopped
                (!isLiveStarted && !isLiveStopped) || isLiveStopped && dispatch(setIsPlaying(undefined));
                isCurrentMediaLive !== isMediaLive && dispatch(setIsMediaLive(isCurrentMediaLive)); // Only set redux store isMediaLive if visible
            }

            if ((!isLiveStarted || isLiveStopped) && isCurrentMediaLive) {
				return;
			}

			setTimeout(() => {
				const { defaults } = scenario ?? {};
				if (defaults?.sceneId && defaults?.sceneId === sceneConfig?.currentScene && changeSceneType === CHANGE_SCENE_TYPES.default) {
					settings?.playbackOptions?.replay && widget?.play?.();
					if (!settings?.playbackOptions?.replay) {
						dispatch(setPlayerCurrentPosition(0));
						dispatch(setIsPlaying(undefined));
                        dispatch(setRemovePoster(false))
					}
                    dispatch(setSceneHistory([]));
				} else {
					widget?.play?.();
				}

                let inTime = isCurrentMediaLive ? LiveHelper.LIVE_POSITION_OFFSET : 0
                if (queryParams?.id === playerMedia && queryParams?.in && mediaType !== 'scenario') {
                    inTime = parseInt(queryParams?.in, 10);
                }
                currentMedia && isCurrentMediaLive !== undefined && widget?.position?.(inTime);
				widget?.volume?.(currentVolumeLevel);
				// (window as any).GoBrain.widgets()[playerId].on('queueItem', () => {}) onlly sent one time when player init
				// call handlePlayerQueueItem here to update player when changed
                if (mediaType === 'scenario') {
                    onPlayerQueueItem({ currentIndex, currentItem: queue?.[0] });
                }

                dispatch(setPlayerCurrentPosition(inTime));
			});
		} else if (!isVisible) {
			setTimeout(() => {
				if (!isCurrentMediaLive || isLiveStarted) {
					widget?.pause?.();
				}
                widget?.position?.(0);
				widget?.volume?.(0);
			}, 200);
		}
    }, [changeSceneType, currentVolumeLevel, dispatch, isMediaLive, isVisible, mediaType, mediasData, onPlayerQueueItem, playerId, playerMedia, queryParams?.id, queryParams?.in, scenario, sceneConfig?.currentScene, settings?.playbackOptions?.replay, token]);

    const onReceiveMessage = useCallback((event: MessageEvent) => {
        const { props, currentPosition } = latestState.current;
        const isFromQVPEditor = (event.origin.includes('video.qbrick.com') || event.origin.includes('localhost:9000'))
                                    && event.data.identifier === MessageHelper.MESSAGE_BASE_IDENTIFIERS.editorIdentifier;
        if (!isFromQVPEditor || !props.isVisible) {
            return;
        }

        if (event.data?.type === 'move-position-from-editor' && event.data?.position !== undefined && event.data?.position !== null) {
            positionMessage.current = event.data?.message;
        }
        if (event.data?.type === 'pause') {
            dispatch(setIsPlaying(false));
        }
        if (event.data?.type === 'play') {
            dispatch(setIsPlaying(true));
        }

        PlayerHelper.onReceiveMessage({event, playerId: props.playerId, currentPosition, isVisible: !!props.isVisible});
    }, [dispatch]);

    const handleEditorPlayer = useCallback(() => {
        if (playerId === EDITOR_PLAYER_ID) {
            const widget = (window as any).GoBrain.widgets()[playerId];
            const currentMedia = mediasData.find((media: any) => media?.id === playerMedia);
            if (!currentMedia?.start) {
                return;
            }
            console.info('NHUNG - Editor player current media:', currentMedia);
            widget?.position?.(currentMedia.start);
            dispatch(setPlayerCurrentPosition(currentMedia.start));
        }
    }, [dispatch, mediasData, playerId, playerMedia]);

	const sendMessageToParent = useCallback((type: string) => {
        const { props, scenario } = latestState.current;
        MessageHelper.sendMessage({ type: type, mediaId: props.playerMedia, scenarioId: scenario?.id, metadata: {} });
	}, []);

    const onPlayerSeek = useCallback(() => sendMessageToParent(MessageHelper.MESSAGE_TYPES.seek), [sendMessageToParent]);
    const onPlayerSeeked = useCallback(() => sendMessageToParent(MessageHelper.MESSAGE_TYPES.seeked), [sendMessageToParent]);

    const playerEvents = {
        queueItem: onPlayerQueueItem,
        playable: onPlayerPlayable,
        play: onPlayerPlay,
        pause: onPlayerPause,
        playComplete: onPlayerPlayComplete,
        position: onPlayerPositionChange,
        duration: onPlayerDurationChange,
        notification: onPlayerNotificationEvent,
        volume: onPlayerVolumeChange,
        error: onPlayerError,
        seek: onPlayerSeek,
        seeked: onPlayerSeeked,
        adsStart: hideControls,
        adsPlay: hideControls,
        adsComplete: showControls,
        adsAllComplete: showControls,
        adsSkipped: showControls,
    };

    const createPlayerInstance = useCallback(() => {
        const { isMediaLive, mediasData, playlistId, scenario, mediaType, props, queryParams } = latestState.current;
        const currentMedia = mediasData.find((media: any) => media?.id === props.playerMedia);
        const isStoryPlaylist = playlistId && queryParams?.configId === STORY_CONFIG_ID;
        const playerProps = { ...props, playlistId, isMediaLive: currentMedia?.live || isMediaLive, isStoryPlaylist };

        const handlePlayerReady = (value: boolean) => {
            createdPlayerInstance.current = true;
            onPlayerReady(value);
			MessageHelper.sendMessage({ type: MessageHelper.MESSAGE_TYPES.ready, mediaId: props.playerMedia, scenarioId: scenario?.id, metadata: {} });
            PlayerHelper.subscribePlayerEvents(props.playerId, playerEvents);
        }
        PlayerHelper.createPlayerInstance({ playerProps, scenario, onPlayerReady: handlePlayerReady, mediaType });
    }, [onPlayerReady]);

	const initializePlayer = useCallback(() => {
        if (createdPlayerInstance.current){
            return;
        };
        if (accountId && (playerMedia || playlistId)) {
            createPlayerInstance();
            !playlistId && playVisiblePlayer();
        }
        (window as any).addEventListener('message', onReceiveMessage);

        return () => (window as any).removeEventListener('message', onReceiveMessage);
    }, [accountId, createPlayerInstance, onReceiveMessage, playerMedia, playlistId]);

    useEffect(() => {
        return () => PlayerHelper.unsubscribePlayerEvents(playerId, playerEvents);
    }, [])

    useEffect(() => {
        if (showCountdownModule && createdPlayerInstance.current) {
            return;
        }

        if (isVisible) {
            const defaultVolume = settings?.playbackOptions?.setDefaultVolume ? settings?.playbackOptions?.defaultVolumeValue / 100 || 0.5 : 0.5;
            const volume =
                playerId === EDITOR_PLAYER_ID
                    ? 0
                    : !queryParams?.mute
                        ? settings?.playbackOptions?.muteAtStart
                            ? 0
                            : defaultVolume
                        : queryParams?.mute === 'true'
                            ? 0
                            : defaultVolume;
            dispatch(setCurrentVolumeLevel(volume));
        }

        initializePlayer();
    }, [token, showCountdownModule]);

    useEffect(() => {
        if (playerId === EDITOR_PLAYER_ID) {
            setLoading(false);
            handleEditorPlayer();
        } else {
            PlayerHelper.setThumbnailForPlayer(thumbnailUrl, (loading: boolean) => setLoading(loading));
        }
    }, [handleEditorPlayer, playerId, thumbnailUrl]);

    useEffect(() => {
        playVisiblePlayer();
    }, [isVisible]);

    return (
        <div
            style={{ visibility: loading ? 'hidden' : 'visible' }}
            id={`${playerId}-container`}
            className={joinClassNames(
                'main-player',
                isVisible && !showCountdownModule ? 'main-player--visible' : 'main-player--hidden',
                isMaintainProportions !== false && queryParams?.fromQplayerWidget !== "true" ? 'qplayer--maintain-proportions' : '',
                minimizedPlayer ? 'qplayer--minimized' : '',
                classNames
            )}
            onTouchEnd={handlePlayerHover}
            aria-hidden={!!showCountdownModule}
        />
    );
}

export default Player;